import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import {
    DEFAULT_TABLE_ROW_OPTIONS,
    DEFAULT_TABLE_ROWS_PER_PAGE
} from '../../../../utils/formValidation/commonConstants';
import { stableSort } from '../../../../utils/sortFunctions';

import QuitReviewRow from './QuitReviewRow';

const headCells = [
    { id: 'eventDate', numeric: false, label: 'Date', align: 'left' },
    { id: 'intervention', numeric: true, label: 'Intervention', align: 'center' },
    { id: 'selfReportedQuit', numeric: false, label: 'Self Reported Quit', align: 'center' },
    { id: 'coValidation', numeric: true, label: 'CO Validation', align: 'center' },
    { id: 'coResult', numeric: true, label: 'CO Result', align: 'center' },
    { id: 'quitReviewSuccessful', numeric: true, label: 'Quit Review Successful', align: 'center' }
];

const QuitReviewTable = ({ rows, isDisabled, isDeleteDisabled, onEdit, onDelete }) => {
    // LOCAL STATE
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('eventDate');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_TABLE_ROWS_PER_PAGE);

    // EVENT HANDLERS
    const onSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(0);
    };

    const onChangePage = (e, newPage) => {
        const page = newPage > 0 ? newPage : 0;
        setPage(page);
    };

    const onChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    // RENDER

    return (
        <Box sx={{ width: '100%' }}>
            <Paper sx={{ width: '100%', mb: 2 }}>
                <TableContainer>
                    <Table sx={{ minWidth: 320 }} aria-labelledby="tableTitle" size="medium">
                        <TableHead>
                            <TableRow>
                                {headCells.map((headCell) => (
                                    <TableCell
                                        key={headCell.id}
                                        align={headCell.align}
                                        sx={{
                                            paddingLeft: headCell.align === 'center' ? '32px' : ''
                                        }}
                                        sortDirection={orderBy === headCell.id ? order : false}>
                                        <TableSortLabel
                                            active={orderBy === headCell.id}
                                            direction={orderBy === headCell.id ? order : 'asc'}
                                            onClick={() => onSort(headCell.id)}>
                                            <Typography variant={'subtitle3'} color={'primary'}>
                                                {headCell.label}
                                            </Typography>
                                            {orderBy === headCell.id ? (
                                                <Box component="span" sx={visuallyHidden}>
                                                    {order === 'desc'
                                                        ? 'sorted descending'
                                                        : 'sorted ascending'}
                                                </Box>
                                            ) : null}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {stableSort(rows, orderBy, order)
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row) => (
                                    <QuitReviewRow
                                        key={row.id}
                                        row={row}
                                        isDisabled={isDisabled}
                                        isDeleteDisabled={isDeleteDisabled}
                                        onEdit={() => onEdit(row)}
                                        onDelete={() => onDelete(row)}
                                    />
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    data-testid="tablePagination"
                    rowsPerPageOptions={DEFAULT_TABLE_ROW_OPTIONS}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={onChangePage}
                    onRowsPerPageChange={onChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
};

QuitReviewTable.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    isDisabled: PropTypes.bool,
    isDeleteDisabled: PropTypes.bool,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func
};

export default QuitReviewTable;
